import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const ExgratiaAmountTable = ({ 
  exgratiaData, 
  canEdit = true, 
  onAmountUpdate = null 
}) => {
  const [editableAmounts, setEditableAmounts] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    if (exgratiaData) {
      const amounts = {};
      Object.keys(exgratiaData).forEach(category => {
        if (exgratiaData[category] && typeof exgratiaData[category] === 'object') {
          amounts[category] = exgratiaData[category].total_exgratia_amount || 0;
        }
      });
      setEditableAmounts(amounts);
    }
  }, [exgratiaData]);

  const handleEditStart = (category) => {
    setEditingCategory(category);
  };

  const handleEditSave = (category) => {
    setEditingCategory(null);
    if (onAmountUpdate) {
      onAmountUpdate(category, editableAmounts[category]);
    }
  };

  const handleEditCancel = (category) => {
    // Reset to original value
    if (exgratiaData?.[category]) {
      setEditableAmounts(prev => ({
        ...prev,
        [category]: exgratiaData[category].total_exgratia_amount || 0
      }));
    }
    setEditingCategory(null);
  };

  const handleAmountChange = (category, value) => {
    const numValue = parseFloat(value) || 0;
    setEditableAmounts(prev => ({
      ...prev,
      [category]: numValue
    }));
  };

  const getTotalExgratiaAmount = () => {
    return Object.values(editableAmounts).reduce((sum, amount) => sum + (amount || 0), 0);
  };

  const getCategoryDisplayName = (category) => {
    const categoryNames = {
      'exgratia_crop': 'Crop Damage',
      'exgratia_house': 'House Damage', 
      'exgratia_land': 'Land Damage',
      'exgratia_livestock': 'Livestock Damage'
    };
    return categoryNames[category] || category.replace('exgratia_', '').replace('_', ' ');
  };

  if (!exgratiaData || Object.keys(exgratiaData).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
        No exgratia amount data available
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.300' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell><strong>Category</strong></TableCell>
            <TableCell><strong>Details</strong></TableCell>
            <TableCell align="right"><strong>Amount (₹)</strong></TableCell>
            {canEdit && <TableCell align="center" sx={{ width: 100 }}><strong>Actions</strong></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(exgratiaData).map(([category, data]) => {
            const isEditing = editingCategory === category;
            const categoryLabel = getCategoryDisplayName(category);
            
            return (
              <TableRow key={category} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {categoryLabel}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ fontSize: '0.875rem' }}>
                    {category === 'exgratia_crop' && (
                      <Typography variant="caption" display="block">
                        Reported Value: ₹{data.reported_approx_crop_value?.toLocaleString('en-IN')}
                      </Typography>
                    )}
                    {category === 'exgratia_house' && (
                      <Box>
                        {data.fully_damaged_amt > 0 && (
                          <Typography variant="caption" display="block">
                            Fully Damaged: ₹{data.fully_damaged_amt?.toLocaleString('en-IN')}
                          </Typography>
                        )}
                        {data.partially_damaged_amt > 0 && (
                          <Typography variant="caption" display="block">
                            Partially Damaged: ₹{data.partially_damaged_amt?.toLocaleString('en-IN')}
                          </Typography>
                        )}
                        {data.severely_damaged_amt > 0 && (
                          <Typography variant="caption" display="block">
                            Severely Damaged: ₹{data.severely_damaged_amt?.toLocaleString('en-IN')}
                          </Typography>
                        )}
                        {data.hut_damaged_amt > 0 && (
                          <Typography variant="caption" display="block">
                            Hut Damaged: ₹{data.hut_damaged_amt?.toLocaleString('en-IN')}
                          </Typography>
                        )}
                        {data.cattleshed_damaged_amt > 0 && (
                          <Typography variant="caption" display="block">
                            Cattleshed Damaged: ₹{data.cattleshed_damaged_amt?.toLocaleString('en-IN')}
                          </Typography>
                        )}
                        {data.clothing_loss_amt > 0 && (
                          <Typography variant="caption" display="block">
                            Clothing Loss: ₹{data.clothing_loss_amt?.toLocaleString('en-IN')}
                          </Typography>
                        )}
                        {data.utensil_loss_amt > 0 && (
                          <Typography variant="caption" display="block">
                            Utensil Loss: ₹{data.utensil_loss_amt?.toLocaleString('en-IN')}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {category === 'exgratia_land' && (
                      <Box>
                        <Typography variant="caption" display="block">
                          Area Affected: {data.area_affected} ha
                        </Typography>
                        <Typography variant="caption" display="block">
                          Calculated: ₹{data.calc_amount?.toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Minimum: ₹{data.min_amount?.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    )}
                    { category === 'exgratia_livestock' && data && (
                      <Box>
                        {
                          data.large_milch_animals_count > 0 && (
                            <Typography variant="caption" display="block">
                              Large Milch Animals: {data.large_milch_animals_count} units
                            </Typography>
                          )
                        }
                        {
                          data.small_milch_animals_count > 0 && (
                            <Typography variant="caption" display="block">
                              Small Milch Animals: {data.small_milch_animals_count} units
                            </Typography>
                          )
                        }
                        {
                          data.large_draught_animals_count > 0 && (
                            <Typography variant="caption" display="block">
                              Large Draught Animals: {data.large_draught_animals_count} units
                            </Typography>
                          )
                        }
                        {
                          data.small_draught_animals_count > 0 && (
                            <Typography variant="caption" display="block">
                              Small Draught Animals: {data.small_draught_animals_count} units
                            </Typography>
                          )
                        }
                        {
                          data.poultry_count > 0 && (
                            <Typography variant="caption" display="block">
                              Poultry: {data.poultry_count} units
                            </Typography>
                          )
                        }
                        {
                          data.calc_amount > 0 && (
                            <Typography variant="caption" display="block">
                              Calculated: ₹{data.calc_amount?.toLocaleString('en-IN')}
                            </Typography>
                          )
                        }
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {isEditing ? (
                    <TextField
                      type="number"
                      value={editableAmounts[category] || 0}
                      onChange={(e) => handleAmountChange(category, e.target.value)}
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      ₹{(editableAmounts[category] || data.total_exgratia_amount || 0).toLocaleString('en-IN')}
                    </Typography>
                  )}
                </TableCell>
                {canEdit && (
                  <TableCell align="center">
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditSave(category)}
                          title="Save"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleEditCancel(category)}
                          title="Cancel"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditStart(category)}
                        title="Edit Amount"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          
          {/* Total Row */}
          <TableRow sx={{ backgroundColor: 'primary.dark', '& td': { borderTop: '2px solid', borderColor: 'primary.main' } }}>
            <TableCell colSpan={canEdit ? 3 : 2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'white' }}>
                Total Exgratia Amount
              </Typography>
            </TableCell>
            <TableCell align="right" colSpan={canEdit ? 1 : 1}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                ₹{getTotalExgratiaAmount().toLocaleString('en-IN')}
              </Typography>
            </TableCell>
            {/* {canEdit && <TableCell></TableCell>} */}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExgratiaAmountTable;
